import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { Footer, GoldDots, Logo } from "@/components/Brand";
import { ChoiceCard } from "@/pages/Baskets";
import { Helplines } from "@/pages/Ready";
import { QUESTIONS } from "@/lib/content";
import { errMsg, startPlanPath, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const Generating = () => (
  <div className="min-h-screen flex flex-col bg-alabaster">
  <div className="flex-1 flex flex-col items-center justify-center px-6 text-center fade-in" data-testid="generating-screen">
    <Logo variant="goldTagline" className="h-24 mb-10" />
    <h1 className="text-2xl sm:text-3xl">Making sense of what you've shared…</h1>
    <p className="mt-4 max-w-md text-ink-soft">Take a breath. We're reflecting on your answers to shape your Action Plan.</p>
    <div className="mt-10"><GoldDots /></div>
  </div>
  <Footer />
  </div>
);

const AreYouOk = ({ onPause, onContinue }) => (
  <AppShell nav={false}>
    <div className="fade-in" data-testid="are-you-ok-screen">
      <h1 className="text-3xl sm:text-4xl">Are you OK?</h1>
      <div className="ahu-card mt-8 p-6 sm:p-8">
        <p className="text-ink leading-relaxed">Do you have supports around you right now? You can contact your nearest helpline any time:</p>
        <Helplines />
        <p className="mt-4 text-ink-soft">AHU isn't a crisis service, and it's OK to stop here and come back when you're ready.</p>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button className="btn-charcoal sm:min-w-[200px]" onClick={onPause} data-testid="crisis-pause-btn">Stop for now</button>
        <button className="btn-outline sm:min-w-[200px]" onClick={onContinue} data-testid="crisis-continue-btn">I'm OK, continue</button>
      </div>
    </div>
  </AppShell>
);

export default function Questions() {
  const api = useApi();
  const nav = useNavigate();
  const { me, refresh } = useMe();
  const [path] = useState(() => startPlanPath(me));
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [alertIds, setAlertIds] = useState([]);
  const [crisis, setCrisis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const q = QUESTIONS[step];
  const a = answers[q.id] || {};
  const last = step === QUESTIONS.length - 1;
  const canNext = q.textFirst ? (a.text || "").trim().length >= 3 : q.choices.length === 0 || !!a.choice;
  const set = (patch) => setAnswers({ ...answers, [q.id]: { ...a, ...patch } });

  const generate = async (ids) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/plans", { answers, alert_ids: ids });
      await refresh();
      nav(`/plans/${data.id}?new=1`, { replace: true });
    } catch (e) {
      setGenerating(false);
      if (e?.response?.status === 402) return nav("/membership");
      if (e?.response?.status === 403) return nav("/not-yet");
      if (e?.response?.status === 409) { toast.message("You have 3 active plans. Complete one to start the next."); return nav("/plans"); }
      toast.error(errMsg(e));
    }
  };

  const advance = (ids = alertIds) => (last ? generate(ids) : (setStep(step + 1), window.scrollTo(0, 0)));

  const next = async () => {
    if (q.freeText && a.text?.trim()) {
      setBusy(true);
      try {
        const { data } = await api.post("/safety/check", { question: q.id, text: a.text });
        if (data.flagged) {
          setAlertIds([...alertIds, data.alert_id]);
          return setCrisis(data.alert_id);
        }
      } catch {} finally { setBusy(false); }
    }
    advance();
  };

  const respond = async (response) => {
    try { await api.post(`/safety/alerts/${crisis}/respond`, { response }); } catch {}
    const ids = alertIds;
    setCrisis(null);
    if (response === "paused") nav("/");
    else advance(ids);
  };

  if (path !== "/ready") return <Navigate to={path} replace />;
  if (generating) return <Generating />;
  if (crisis) return <AreYouOk onPause={() => respond("paused")} onContinue={() => respond("continued")} />;

  return (
    <AppShell nav={false}>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <button className="p-2 -ml-2 text-charcoal disabled:opacity-0" disabled={step === 0} onClick={() => setStep(step - 1)} aria-label="Back" data-testid="question-back-btn"><ArrowLeft size={22} /></button>
          <span className="text-sm text-ink-soft" data-testid="question-counter">{step + 1} of {QUESTIONS.length}</span>
        </div>
        <div className="h-1 w-full rounded-full bg-rule overflow-hidden" data-testid="question-progress">
          <div className="h-full bg-sage" style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%`, transition: "width .8s ease" }} />
        </div>
      </div>
      <div key={q.id} className="fade-in" data-testid={`question-${q.id}`}>
        {step === 0 && <p className="text-ink-soft mb-3" data-testid="question-intro">Change takes time. Move at your own pace.</p>}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl leading-snug">{q.text}</h1>
        {q.freeText && (
          <div className="mt-8">
            <label className="block text-sm font-medium text-charcoal mb-2" htmlFor={`${q.id}-text`}>{q.textFirst ? "In your own words" : "Your words"}{q.optional && <span className="text-ink-soft font-normal"> (optional)</span>}</label>
            <textarea id={`${q.id}-text`} rows={6} maxLength={4000} className="field resize-y leading-relaxed" placeholder={q.placeholder}
              value={a.text || ""} onChange={(e) => set({ text: e.target.value })} data-testid={`${q.id}-text-input`} />
            <p className="mt-2 text-sm text-ink-soft" data-testid={`${q.id}-medical-note`}>Please don't include medical details or health records.</p>
            <p className="mt-1 text-sm text-ink-soft md:hidden">Tip: tap the microphone on your keyboard to talk instead of typing.</p>
          </div>
        )}
        {q.choices.length > 0 && (
          <div className="mt-8">
            {q.textFirst && <p className="text-sm font-medium text-charcoal mb-3" data-testid={`${q.id}-choices-label`}>Which of these closely relates to your situation? <span className="text-ink-soft font-normal">(optional)</span></p>}
            <div className="grid gap-3">
              {q.choices.map((c, i) => (
                <ChoiceCard key={c} selected={a.choice === c} onClick={() => set({ choice: a.choice === c && q.textFirst ? null : c })} testId={`${q.id}-choice-${i}`}>{c}</ChoiceCard>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="mt-10">
        {last ? (
          <button className="btn-gold w-full sm:w-auto sm:min-w-[280px]" disabled={busy} onClick={next} data-testid="get-plan-btn">Get my AHU Action Plan</button>
        ) : (
          <button className="btn-charcoal w-full sm:w-auto sm:min-w-[200px]" disabled={!canNext || busy} onClick={next} data-testid="question-next-btn">Next</button>
        )}
      </div>
    </AppShell>
  );
}
